import { useEffect, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Warehouse, Truck } from "@phosphor-icons/react";

const GROUPS = [["all", "All"], ["vehicle", "Vehicles"], ["plant", "Plant & Equipment"], ["kit", "Kits & Containers"], ["trailer", "Trailers"], ["hired", "Hired In"]];

export default function GaragePage() {
  const [assets, setAssets] = useState([]);
  const [group, setGroup] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/assets"), api.get("/config/asset-types").catch(() => ({ data: [] }))])
      .then(([a, t]) => {
        const types = Object.fromEntries((t.data.items || t.data || []).map((x) => [x.id, x.group || "other"]));
        const list = (a.data.items || a.data || []).map((x) => ({ ...x, _group: x.is_hired ? "hired" : (types[x.asset_type_id] || "other") }));
        setAssets(list);
      }).finally(() => setLoading(false));
  }, []);

  const filtered = group === "all" ? assets : assets.filter((a) => a._group === group);
  // Buckets: Currently out = last confirmed inside a vehicle/asset; Ready = elsewhere / at a location.
  const out = filtered.filter((a) => a.last_confirmed_place_type === "asset");
  const ready = filtered.filter((a) => a.last_confirmed_place_type !== "asset" && !a.location_discrepancy);
  const attention = filtered.filter((a) => a.location_discrepancy);

  const Row = ({ a }) => (
    <Link to={`/assets/${a.id}`} data-testid={`garage-item-${a.asset_number}`} className="flex items-center justify-between bg-white border rounded-lg px-3 py-2 hover:border-blue-400">
      <span className="text-sm font-semibold text-slate-800">{a.asset_number} — {a.name}</span>
      <span className="text-xs text-slate-500">{a.last_confirmed_place_label || "—"}</span>
    </Link>
  );
  const Bucket = ({ title, items, tone }) => items.length === 0 ? null : (
    <div className="flex flex-col gap-2"><h3 className={`font-semibold ${tone || "text-slate-700"}`}>{title} ({items.length})</h3>{items.map((a) => <Row key={a.id} a={a} />)}</div>
  );

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 flex flex-col gap-6" data-testid="garage-page">
      <div className="flex items-center gap-3"><Warehouse size={30} weight="bold" className="text-blue-600" />
        <div><h1 className="font-display text-3xl font-black text-slate-900">Garage / Stores</h1>
          <p className="text-sm text-slate-500">Grouped equipment register — what is where.</p></div></div>
      <div className="flex flex-wrap gap-2">
        {GROUPS.map(([v, l]) => <button key={v} data-testid={`garage-group-${v}`} onClick={() => setGroup(v)} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${group === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>{l}</button>)}
        <Link to="/assets" className="px-3 py-1.5 rounded-full text-sm font-semibold bg-blue-50 text-blue-700 ml-auto">Full register →</Link>
      </div>
      {loading ? <p className="text-slate-500">Loading…</p> : (
        <div className="grid gap-6 md:grid-cols-2">
          <Bucket title="Ready / Available" items={ready} tone="text-green-700" />
          <Bucket title="Currently out (in vehicles/teams)" items={out} tone="text-blue-700" />
          <Bucket title="Needs attention" items={attention} tone="text-red-700" />
          {filtered.length === 0 && <p className="text-slate-500">No assets in this group.</p>}
        </div>
      )}
    </div>
  );
}
